import { useState } from 'react'
import {
  Search,
  TrendingUp,
  TrendingDown,
  Building2,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Coins,
} from 'lucide-react'
import { usePriceSpreads } from '@/hooks/useSecurity'
import { formatNumber, formatCurrency, cn } from '@/lib/utils'
import type { PriceSpread, ListingType } from '@/types/security'

/**
 * Securities List Page - displays all listed securities with prices.
 *
 * Features:
 * - Paginated list of securities
 * - Search by name/identifier
 * - Sortable columns
 * - Bid/Ask spread display
 * - Links to security details
 */

type SortField = 'name' | 'lastPrice' | 'spreadPercent' | 'type'
type SortDirection = 'asc' | 'desc'

export function SecuritiesPage() {
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const pageSize = 20

  // Debounce search input
  const handleSearchChange = (value: string) => {
    setSearch(value)
    // Simple debounce
    setTimeout(() => {
      setDebouncedSearch(value)
      setPage(0) // Reset to first page on search
    }, 300)
  }

  const sortParam = `${sortField},${sortDirection}`

  const { data, isLoading, error, isFetching } = usePriceSpreads({
    page,
    size: pageSize,
    search: debouncedSearch || undefined,
    sort: sortParam,
  })

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
    setPage(0)
  }

  const totalPages = data?.totalPages || 0
  const totalElements = data?.totalElements || 0

  return (
    <div className="flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-text-dark">Securities</h1>
            <p className="text-sm text-text-muted">
              {totalElements > 0
                ? `${totalElements} listed securities`
                : 'Browse all listed securities'}
            </p>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search by name or identifier..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary sm:w-64"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 sm:p-6">
        {/* Loading state */}
        {isLoading && (
          <div className="flex h-64 items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <p className="text-text-light">Loading securities...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
            <p className="text-red-600">Failed to load securities. Please try again.</p>
          </div>
        )}

        {/* Data table */}
        {data && (
          <>
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <SortableHeader
                      label="Security"
                      field="name"
                      currentSort={sortField}
                      direction={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Type"
                      field="type"
                      currentSort={sortField}
                      direction={sortDirection}
                      onSort={handleSort}
                      className="hidden sm:table-cell"
                    />
                    <SortableHeader
                      label="Last Price"
                      field="lastPrice"
                      currentSort={sortField}
                      direction={sortDirection}
                      onSort={handleSort}
                      className="text-right"
                    />
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-muted">
                      Bid
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-muted">
                      Ask
                    </th>
                    <SortableHeader
                      label="Spread"
                      field="spreadPercent"
                      currentSort={sortField}
                      direction={sortDirection}
                      onSort={handleSort}
                      className="hidden text-right md:table-cell"
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.content.map((security) => (
                    <SecurityRow key={security.id} security={security} />
                  ))}
                  {data.content.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                        {debouncedSearch
                          ? 'No securities match your search.'
                          : 'No securities found.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-text-muted">
                  Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, totalElements)} of{' '}
                  {totalElements}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="rounded-md border border-gray-300 p-2 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-text-muted">
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="rounded-md border border-gray-300 p-2 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Loading overlay for refetching */}
            {isFetching && !isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/50">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// Sortable table header component
interface SortableHeaderProps {
  label: string
  field: SortField
  currentSort: SortField
  direction: SortDirection
  onSort: (field: SortField) => void
  className?: string
}

function SortableHeader({
  label,
  field,
  currentSort,
  direction,
  onSort,
  className,
}: SortableHeaderProps) {
  const isActive = currentSort === field
  const SortIcon = isActive ? (direction === 'asc' ? ArrowUp : ArrowDown) : ArrowDown

  return (
    <th
      className={cn(
        'cursor-pointer px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-muted hover:text-text-dark',
        className
      )}
      onClick={() => onSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        <SortIcon className={cn('h-3 w-3', !isActive && 'opacity-30')} />
      </span>
    </th>
  )
}

// Security row component
interface SecurityRowProps {
  security: PriceSpread
}

function SecurityRow({ security }: SecurityRowProps) {
  const {
    name,
    securityIdentifier,
    type,
    lastPrice,
    bidPrice,
    bidSize,
    askPrice,
    askSize,
    spreadPercent,
  } = security

  // Determine link - for now, link to v1 security page
  const v1BaseUrl = typeof window !== 'undefined' ? window.location.origin : ''
  const detailUrl = `${v1BaseUrl}/security/${securityIdentifier}`

  return (
    <tr className="hover:bg-gray-50">
      {/* Security name and identifier */}
      <td className="px-4 py-3">
        <a href={detailUrl} className="group">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
              <TypeIcon type={type} />
            </div>
            <div>
              <p className="font-medium text-text-dark group-hover:text-primary">{name}</p>
              <p className="text-xs text-text-muted">{securityIdentifier}</p>
            </div>
          </div>
        </a>
      </td>

      {/* Type */}
      <td className="hidden px-4 py-3 sm:table-cell">
        <TypeBadge type={type} />
      </td>

      {/* Last price */}
      <td className="px-4 py-3 text-right">
        {lastPrice != null ? (
          <span className="font-medium">{formatCurrency(lastPrice)}</span>
        ) : (
          <span className="text-text-muted">-</span>
        )}
      </td>

      {/* Bid */}
      <td className="px-4 py-3 text-right">
        {bidPrice != null ? (
          <div>
            <span className="font-medium text-success">{formatCurrency(bidPrice)}</span>
            {bidSize != null && (
              <p className="text-xs text-text-muted">{formatNumber(bidSize)} pcs</p>
            )}
          </div>
        ) : (
          <span className="text-text-muted">-</span>
        )}
      </td>

      {/* Ask */}
      <td className="px-4 py-3 text-right">
        {askPrice != null ? (
          <div>
            <span className="font-medium text-red-500">{formatCurrency(askPrice)}</span>
            {askSize != null && (
              <p className="text-xs text-text-muted">{formatNumber(askSize)} pcs</p>
            )}
          </div>
        ) : (
          <span className="text-text-muted">-</span>
        )}
      </td>

      {/* Spread */}
      <td className="hidden px-4 py-3 text-right md:table-cell">
        {spreadPercent != null ? (
          <span
            className={cn('font-medium', spreadPercent > 10 ? 'text-red-500' : 'text-text-dark')}
          >
            {spreadPercent.toFixed(2)}%
          </span>
        ) : (
          <span className="text-text-muted">-</span>
        )}
      </td>
    </tr>
  )
}

// Type icon component
function TypeIcon({ type }: { type: ListingType }) {
  switch (type) {
    case 'STOCK':
      return <Building2 className="h-5 w-5 text-primary" />
    case 'BOND':
    case 'INTEREST_TENDER_BOND':
    case 'SYSTEM_BOND':
      return <TrendingUp className="h-5 w-5 text-yellow-500" />
    case 'DERIVATIVE_WARRANT':
      return <TrendingDown className="h-5 w-5 text-purple-500" />
    case 'COIN':
      return <Coins className="h-5 w-5 text-amber-500" />
    default:
      return <TrendingUp className="h-5 w-5 text-text-muted" />
  }
}

// Type badge component
function TypeBadge({ type }: { type: ListingType }) {
  const config: Record<ListingType, { label: string; className: string }> = {
    STOCK: { label: 'Stock', className: 'bg-primary-100 text-primary' },
    BOND: { label: 'Bond', className: 'bg-yellow-100 text-yellow-700' },
    INTEREST_TENDER_BOND: { label: 'IT Bond', className: 'bg-yellow-100 text-yellow-700' },
    SYSTEM_BOND: { label: 'System Bond', className: 'bg-yellow-100 text-yellow-700' },
    REPO: { label: 'Repo', className: 'bg-gray-100 text-gray-700' },
    SYSTEM_REPO: { label: 'System Repo', className: 'bg-gray-100 text-gray-700' },
    DERIVATIVE_WARRANT: { label: 'Warrant', className: 'bg-purple-100 text-purple-700' },
    COIN: { label: 'Coin', className: 'bg-amber-100 text-amber-700' },
  }

  const { label, className } = config[type] || {
    label: type,
    className: 'bg-gray-100 text-gray-700',
  }

  return (
    <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', className)}>{label}</span>
  )
}
